import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Trash2,
    Eye,
    Video,
    Users,
    Clock,
    Ban,
    CheckCircle,
    Loader2,
    RefreshCcw,
    Search,
    X,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";

const toTitleCase = (value) => {
    const s = String(value || "");
    if (!s) return "-";
    return s.charAt(0).toUpperCase() + s.slice(1);
};

const isImageUrl = (value) => /^https?:\/\//i.test(String(value || ""));

const formatDuration = (startedAt, endedAt) => {
    const start = startedAt ? new Date(startedAt) : null;
    if (!start || Number.isNaN(start.getTime())) return "-";
    const end = endedAt ? new Date(endedAt) : new Date();
    if (Number.isNaN(end.getTime())) return "-";
    const diffMs = Math.max(end - start, 0);
    const totalMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hours <= 0) return `${mins}m`;
    return `${hours}h ${mins}m`;
};

export default function LiveStreaming() {
    const [streams, setStreams] = useState([]);
    const [filterStatus, setFilterStatus] = useState("all");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [serverStats, setServerStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Multi-delete state
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    const handleFilterChange = (newStatus) => {
        setFilterStatus(newStatus);
        setPage(1);
        setSelectedIds([]);
    };

    const fetchStreams = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const { data } = await API.get("/admin/live-streams", {
                params: {
                    status: filterStatus,
                    search: debouncedSearch,
                    page,
                    limit,
                },
            });

            if (!data?.success) throw new Error(data?.message || "Failed to load live streams");
            setStreams(Array.isArray(data.streams) ? data.streams : []);
            setTotal(Number(data.total || 0));
            setTotalPages(Number(data.totalPages || 1));
            if (data.stats) {
                setServerStats(data.stats);
            }
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Failed to load live streams");
        } finally {
            setLoading(false);
        }
    }, [filterStatus, debouncedSearch, page, limit]);

    useEffect(() => {
        fetchStreams();
    }, [fetchStreams]);

    const handleToggleSelectAll = () => {
        if (selectedIds.length === streams.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(streams.map((s) => s._id));
        }
    };

    const handleToggleSelectRow = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleBatchDelete = async () => {
        try {
            setIsBatchDeleting(true);
            const { data } = await API.post("/admin/batch-delete", {
                entity: "liveStreams",
                ids: selectedIds,
            });
            if (data?.success) {
                setSelectedIds([]);
                fetchStreams();
            }
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Batch delete failed");
        } finally {
            setIsBatchDeleting(false);
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            await API.patch(`/admin/live-streams/${id}/status`, { status });
            fetchStreams();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to update stream status");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this stream?")) return;
        try {
            await API.delete(`/admin/live-streams/${id}`);
            setSelectedIds((prev) => prev.filter((i) => i !== id));
            fetchStreams();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to delete stream");
        }
    };

    const stats = useMemo(() => {
        if (serverStats) {
            return {
                liveNow: serverStats.liveNow ?? 0,
                totalViewers: serverStats.totalViewers ?? 0,
                totalStreams: serverStats.totalStreams ?? total,
                blocked: serverStats.blocked ?? 0,
            };
        }
        const liveNow = streams.filter((s) => s.status === "live").length;
        const totalViewers = streams.reduce((sum, s) => sum + Number(s.viewerCount || 0), 0);
        const blocked = streams.filter((s) => s.status === "blocked").length;
        return {
            liveNow,
            totalViewers,
            totalStreams: total || streams.length,
            blocked,
        };
    }, [serverStats, streams, total]);

    return (
        <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Live Streaming Management</h1>
                <button
                    onClick={fetchStreams}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
                >
                    <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin text-pink-500' : ''}`} />
                    Refresh Data
                </button>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300 text-sm border border-red-200 dark:border-red-500/20">
                    {error}
                </div>
            )}

            {/* Stats Dashboard */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Live Now</p>
                            <p className="text-2xl font-bold text-red-500 mt-1">{stats.liveNow}</p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
                            <Video className="w-5 h-5 text-red-500" />
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Viewers</p>
                            <p className="text-2xl font-bold text-blue-500 mt-1">{stats.totalViewers.toLocaleString()}</p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
                            <Users className="w-5 h-5 text-blue-500" />
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Streams</p>
                            <p className="text-2xl font-bold text-green-500 mt-1">{stats.totalStreams}</p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-green-50 dark:bg-green-500/10 flex items-center justify-center">
                            <Video className="w-5 h-5 text-green-500" />
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Blocked</p>
                            <p className="text-2xl font-bold text-amber-500 mt-1">{stats.blocked}</p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center">
                            <Ban className="w-5 h-5 text-amber-500" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filters Bar */}
            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Status Tabs */}
                <div className="flex items-center overflow-x-auto hide-scrollbar gap-2">
                    {[
                        { id: "all", label: "All Streams" },
                        { id: "live", label: "Live Now" },
                        { id: "ended", label: "Ended" },
                        { id: "blocked", label: "Blocked" },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => handleFilterChange(tab.id)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                filterStatus === tab.id
                                    ? "bg-pink-50 text-pink-600 border border-pink-200 dark:bg-pink-500/10 dark:text-pink-400 dark:border-pink-500/20 shadow-sm font-semibold"
                                    : "bg-transparent text-gray-600 dark:text-gray-300 border border-transparent hover:bg-gray-100 dark:hover:bg-slate-700"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Search & Limit Control */}
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative flex-1 md:w-72">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search title, host, category..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 dark:bg-slate-900/60 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all"
                        />
                        {search && (
                            <button
                                onClick={() => setSearch("")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    <select
                        value={limit}
                        onChange={(e) => {
                            setLimit(Number(e.target.value));
                            setPage(1);
                        }}
                        className="py-2 px-3 text-sm bg-gray-50 dark:bg-slate-900/60 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 cursor-pointer"
                        title="Items per page"
                    >
                        <option value={10}>10 / page</option>
                        <option value={15}>15 / page</option>
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                    </select>
                </div>
            </div>

            {/* Table View */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left whitespace-nowrap">
                        <thead className="text-xs text-gray-600 dark:text-gray-400 uppercase bg-gray-50/80 dark:bg-slate-900/50 border-b border-gray-200 dark:border-slate-700">
                            <tr>
                                <th className="px-4 py-4 w-12 text-center">
                                    <input
                                        type="checkbox"
                                        checked={streams.length > 0 && selectedIds.length === streams.length}
                                        onChange={handleToggleSelectAll}
                                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                    />
                                </th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Stream Details</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Host</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Metrics</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold">Status</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-pink-500" />
                                        <p>Loading streams data...</p>
                                    </td>
                                </tr>
                            ) : streams.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="w-12 h-12 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-3">
                                                <Video className="w-6 h-6 text-gray-400" />
                                            </div>
                                            <p className="text-base font-medium">No streams found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                streams.map((stream) => {
                                    const hostName = stream?.host?.name || stream?.host?.username || "Unknown";
                                    const avatar = stream?.host?.mainImage || "";
                                    const status = String(stream?.status || "").toLowerCase();
                                    const isLive = status === "live";
                                    const isSelected = selectedIds.includes(stream._id);

                                    return (
                                        <tr key={stream._id} className={`transition-colors ${isSelected ? "bg-pink-50/60 dark:bg-pink-500/10" : "hover:bg-gray-50/50 dark:hover:bg-slate-800/50"}`}>
                                            {/* Checkbox */}
                                            <td className="px-4 py-4 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleToggleSelectRow(stream._id)}
                                                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                                />
                                            </td>

                                            {/* Stream Details */}
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-pink-50 dark:bg-pink-500/10 border border-pink-200/50 dark:border-pink-500/20 text-pink-600 dark:text-pink-400 flex items-center justify-center flex-shrink-0">
                                                        <Video className="w-5 h-5" />
                                                    </div>
                                                    <div className="max-w-[220px]">
                                                        <p className="font-semibold text-gray-900 dark:text-white truncate" title={stream.title}>
                                                            {stream.title || "Untitled stream"}
                                                        </p>
                                                        <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-[11px] font-medium text-gray-600 dark:text-gray-300 rounded">
                                                            {stream.category || "General"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Host */}
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                                <div className="flex items-center gap-2">
                                                    {isImageUrl(avatar) ? (
                                                        <img src={avatar} alt={hostName} className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-slate-600" />
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-pink-100 dark:bg-pink-500/20 text-pink-600 flex items-center justify-center text-xs font-bold border border-pink-200 dark:border-pink-500/20">
                                                            {String(hostName).slice(0, 1).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <span className="font-medium text-gray-700 dark:text-gray-200">{hostName}</span>
                                                </div>
                                            </td>

                                            {/* Metrics */}
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                                <div className="flex flex-col gap-1.5">
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                                                        <Users className="w-3.5 h-3.5 text-blue-500" />
                                                        <span>{Number(stream.viewerCount || 0).toLocaleString()} viewers</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                                                        <Clock className="w-3.5 h-3.5 text-orange-500" />
                                                        <span>{formatDuration(stream.startedAt, stream.endedAt)}</span>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                                                    isLive ? "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400 border border-red-100 dark:border-red-500/20" :
                                                    status === "blocked" ? "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400 border border-gray-200 dark:border-slate-600" :
                                                    "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400 border border-green-100 dark:border-green-500/20"
                                                }`}>
                                                    {isLive && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>}
                                                    {status === "blocked" && <Ban className="w-3 h-3" />}
                                                    {toTitleCase(status)}
                                                </span>
                                            </td>

                                            {/* Actions */}
                                            <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {status === "blocked" ? (
                                                        <button
                                                            onClick={() => handleStatusChange(stream._id, "live")}
                                                            className="p-1.5 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-500/10 rounded transition-colors"
                                                            title="Unblock Stream"
                                                        >
                                                            <CheckCircle className="w-4 h-4" />
                                                        </button>
                                                    ) : isLive ? (
                                                        <button
                                                            onClick={() => handleStatusChange(stream._id, "blocked")}
                                                            className="p-1.5 text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-500/10 rounded transition-colors"
                                                            title="Block Stream"
                                                        >
                                                            <Ban className="w-4 h-4" />
                                                        </button>
                                                    ) : null}

                                                    <button
                                                        onClick={() => alert(`Host: ${hostName}\nStatus: ${toTitleCase(status)}\nStarted: ${stream.startedAt ? new Date(stream.startedAt).toLocaleString() : "-"}`)}
                                                        className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10 rounded transition-colors"
                                                        title="View Details"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    
                                                    <button
                                                        onClick={() => handleDelete(stream._id)}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 rounded transition-colors"
                                                        title="Delete Stream"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {total > 0 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-900/30">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Showing{" "}
                            <span className="font-semibold text-gray-900 dark:text-white">
                                {Math.min((page - 1) * limit + 1, total)}
                            </span>
                            {" "}to{" "}
                            <span className="font-semibold text-gray-900 dark:text-white">
                                {Math.min(page * limit, total)}
                            </span>
                            {" "}of{" "}
                            <span className="font-semibold text-gray-900 dark:text-white">{total}</span>
                            {" "}streams
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    setPage((prev) => Math.max(prev - 1, 1));
                                    setSelectedIds([]);
                                }}
                                disabled={page <= 1 || loading}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </button>
                            <span className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-pink-50 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400 border border-pink-200 dark:border-pink-500/20">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                onClick={() => {
                                    setPage((prev) => Math.min(prev + 1, totalPages));
                                    setSelectedIds([]);
                                }}
                                disabled={page >= totalPages || loading}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Floating Multi-Delete Bar */}
            <BatchActionBar
                selectedCount={selectedIds.length}
                totalCount={streams.length}
                onClearSelection={() => setSelectedIds([])}
                onSelectAll={handleToggleSelectAll}
                onDeleteSelected={handleBatchDelete}
                isDeleting={isBatchDeleting}
                entityName="streams"
            />
        </div>
    );
}